import { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Home } from "lucide-react";
import { AuthContext, API } from "../App";

// Poll the backend up to ~30s for the Stripe session to flip to "paid".
const MAX_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 1500;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [state, setState] = useState({
    loading: true,
    attempts: 0,
    result: null, // { payment_status, kind, diamonds, fchips, amount_dollars, credited }
    error: null,
    timedOut: false,
  });

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState((s) => ({ ...s, loading: false, error: "Missing session_id" }));
      return;
    }
    let attempt = 0;
    while (attempt < MAX_ATTEMPTS) {
      attempt += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setState((s) => ({ ...s, attempts: attempt, result: res.data, loading: true }));
        if (res.data?.payment_status === "paid") {
          setState((s) => ({ ...s, loading: false, result: res.data }));
          try { sessionStorage.removeItem("pending_checkout_session"); } catch (_e) { /* noop */ }
          return;
        }
      } catch (err) {
        setState((s) => ({ ...s, loading: false, error: err.response?.data?.detail || "Failed to fetch status" }));
        return;
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    setState((s) => ({ ...s, loading: false, timedOut: true }));
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);

  const paid = state.result?.payment_status === "paid";
  const kind = state.result?.kind;
  const diamonds = state.result?.diamonds;
  const fchips = state.result?.fchips;
  const amount = state.result?.amount_dollars;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-yellow-900 flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 text-center">
        {state.loading && !paid && (
          <>
            <Loader2 className="w-14 h-14 text-purple-700 mx-auto animate-spin mb-3" />
            <h1 className="text-xl font-black text-purple-900">Confirming your payment…</h1>
            <p className="text-sm text-zinc-600 mt-2">Waiting for Stripe (attempt {state.attempts}/{MAX_ATTEMPTS})</p>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-3" />
            <h1 className="text-2xl font-black text-purple-900" data-testid="payment-success-title">Payment complete!</h1>
            {kind === "diamond_purchase" && (
              <p className="text-sm text-zinc-700 mt-2" data-testid="payment-success-summary">
                <strong className="text-cyan-700">{Number(diamonds || 0).toLocaleString()} 💎</strong>{" "}
                have been added to your account for <strong>${amount}</strong>.
              </p>
            )}
            {kind === "fchips_purchase" && (
              <p className="text-sm text-zinc-700 mt-2" data-testid="payment-success-summary">
                <strong className="text-green-700">{Number(fchips || 0).toLocaleString()} F-Chips</strong>{" "}
                have been added to your account for <strong>${amount}</strong>.
              </p>
            )}
            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() => navigate(token ? "/" : "/login")}
                className="w-full py-2.5 rounded-full bg-gradient-to-br from-purple-700 to-purple-900 text-white font-black text-sm shadow"
                data-testid="payment-success-back-btn"
              >
                <Home className="w-4 h-4 inline mr-1" /> Back to MEGA! Chat
              </button>
            </div>
          </>
        )}
        {!state.loading && !paid && state.error && (
          <>
            <XCircle className="w-14 h-14 text-red-600 mx-auto mb-3" />
            <h1 className="text-xl font-black text-red-800">Couldn&apos;t confirm the payment</h1>
            <p className="text-sm text-zinc-700 mt-2">{state.error}</p>
            <p className="text-xs text-zinc-500 mt-3">If your card was charged, contact support with this session id:<br/><code className="text-xs">{sessionId}</code></p>
            <Link to="/" className="inline-block mt-6 py-2 px-4 rounded-full bg-purple-800 text-white text-sm font-bold" data-testid="payment-error-home-btn">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> Home
            </Link>
          </>
        )}
        {!state.loading && !paid && !state.error && state.timedOut && (
          <>
            <Loader2 className="w-14 h-14 text-yellow-600 mx-auto mb-3" />
            <h1 className="text-xl font-black text-yellow-800">Still processing…</h1>
            <p className="text-sm text-zinc-700 mt-2">Stripe hasn&apos;t confirmed the payment yet. It usually finishes within a minute. Your balance will update automatically the next time you open your account.</p>
            <button
              onClick={() => { setState({ loading: true, attempts: 0, result: null, error: null, timedOut: false }); poll(); }}
              className="mt-4 w-full py-2 rounded-full bg-purple-800 text-white font-bold text-sm"
              data-testid="payment-retry-poll-btn"
            >
              Check again
            </button>
            <Link to="/" className="inline-block mt-3 text-xs text-purple-700 underline" data-testid="payment-back-home-link">Return home</Link>
          </>
        )}
      </div>
    </div>
  );
}
